/**
 * "Lost device" (docs/e2e.md "Phone with one Face ID"): revoking a lost phone alone doesn't stop
 * anyone holding its **synced passkey** (and the phone's passcode) from signing back in on another
 * phone with one Face ID. So the flow revokes the device and, by default, the passkey that trusted
 * it, in one passkey-signed revocation list that another passkey signs (a passkey can't revoke
 * itself). The revocation ends its sessions, removes its PRF escrow and rotates to a new epoch.
 */
import type { Api } from './api.js'
import type { BrowserContentSession } from './content-session.js'
import { endorseDevice, loadDevice } from './device-key.js'
import type { PasskeyProvider } from './passkeys.js'

export interface LostDevicePlan {
  deviceId: string
  /** The passkey that endorsed the lost device; revoked with it unless the user unticks it. */
  credentialId?: string
  /** Live passkeys other than `credentialId`: they sign the revocation. */
  signWith: string[]
}

/** Which passkey endorsed `deviceId`, and which others can sign (never the revoked one). */
export function planLostDevice(
  device: { id: string; credential_id: string },
  credentials: { id: string; revoked?: boolean }[],
  revokePasskey: boolean,
): LostDevicePlan {
  const live = credentials.filter((c) => !c.revoked)
  return {
    deviceId: device.id,
    ...(revokePasskey ? { credentialId: device.credential_id } : {}),
    signWith: live.map((c) => c.id).filter((id) => !revokePasskey || id !== device.credential_id),
  }
}

export async function revokeLostDevice(
  plan: LostDevicePlan,
  deps: {
    session: BrowserContentSession
    api: Pick<Api, 'registerDevice' | 'revokeDevice'>
    passkeys: PasskeyProvider
  },
) {
  if (plan.credentialId && !plan.signWith.length)
    throw new Error('Add another passkey first: it signs the revocation of the lost one.')
  if (plan.credentialId) {
    // This browser may have been trusted by the same synced passkey: re-trust it with a surviving
    // passkey first, or the revocation would also revoke this browser.
    const mine = await loadDevice()
    if (mine?.endorsement?.assertion.credentialId === plan.credentialId)
      await endorseDevice(deps.passkeys, (e) => deps.api.registerDevice(e), {
        allowCredentials: plan.signWith,
      })
  }
  const result = await deps.session.revoke(
    {
      devices: [plan.deviceId],
      ...(plan.credentialId ? { credentials: [plan.credentialId] } : {}),
    },
    plan.credentialId ? { signWith: plan.signWith } : {},
  )
  // Hub-local block too (already implied by the signed list; harmless when repeated).
  await deps.api.revokeDevice(plan.deviceId).catch(() => {})
  return result
}
